import { useCallback, useId, useState } from 'react';

import { type DeliveryRoute } from './useDeliveryRoutes';

type Tabs = {
  /** The route being shown. Never undefined: the first one until chosen. */
  selected: DeliveryRoute;
  selectedIndex: number;
  select: (index: number) => void;
  /** Ties each tab to the single panel beside it, for assistive technology. */
  tabId: (index: number) => string;
  panelId: string;
};

/**
 * Which destination the price panel is showing.
 *
 * Index rather than destination name as the state: the names come from remote
 * settings, so a renamed destination would otherwise leave the panel pointing
 * at nothing. The index is clamped on read instead, which also covers the live
 * settings arriving with fewer destinations than the bundled copy had.
 */
export function useDeliveryRouteTabs(routes: readonly DeliveryRoute[]): Tabs {
  const [index, setIndex] = useState(0);
  const base = useId();

  const selectedIndex = Math.min(index, Math.max(routes.length - 1, 0));
  const select = useCallback((next: number) => setIndex(next), []);
  const tabId = useCallback((position: number) => `${base}-tab-${position}`, [base]);

  return {
    selected: routes[selectedIndex],
    selectedIndex,
    select,
    tabId,
    panelId: `${base}-panel`,
  };
}
